"use client";

/**
 * R3-33 S4-E Phase 3 — AI 비서 전체 페이지.
 * 좌: 세션 목록 / 우: 채팅 화면 (PC), 모바일은 single column.
 */
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { PageHeader } from "@/components/ui/PageHeader";
import { parseMessage } from "@/lib/chatMarkers";
import { ChatCards } from "@/components/chat/ChatCards";
import { FeedbackButtons } from "@/components/chat/FeedbackButtons";

interface SessionSummary {
  id: number;
  title: string | null;
  created_at: string;
  last_message_at: string | null;
  message_count: number;
}

interface MessageOut {
  id: number;
  role: "user" | "assistant" | "tool";
  content: string | null;
  tool_name: string | null;
  created_at: string;
  feedback?: "up" | "down" | null;
}

interface SendOut {
  session_id: number;
  user_message_id: number;
  assistant_message_id: number;
  assistant_text: string;
  tool_calls_used: { tool_name: string; args: Record<string, unknown> }[];
  elapsed_sec: number;
  cost_krw: number;
  quota_remaining: number;
}

interface QuotaInfo {
  tier: string;
  daily_quota: number;
  used_today: number;
  remaining: number;
  resets_at: string;
}

function relTime(s: string | null): string {
  if (!s) return "";
  const d = new Date(s);
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "방금";
  if (diff < 3600) return `${Math.floor(diff / 60)}분`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간`;
  return d.toLocaleDateString("ko-KR");
}

export default function ChatPage() {
  const { token, isLoggedIn } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<MessageOut[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const auth = token ? { Authorization: `Bearer ${token}` } : undefined;

  const reloadSessions = async () => {
    if (!token) return;
    try {
      const r = await axios.get<SessionSummary[]>("/api/chat/sessions", { headers: auth });
      setSessions(r.data);
      if (!activeId && r.data.length > 0) setActiveId(r.data[0].id);
    } catch {
      setSessions([]);
    }
  };

  const reloadQuota = async () => {
    if (!token) return;
    try {
      const r = await axios.get<QuotaInfo>("/api/chat/quota", { headers: auth });
      setQuota(r.data);
    } catch { /* noop */ }
  };

  const loadSession = async (id: number) => {
    if (!token) return;
    try {
      const r = await axios.get<{ messages: MessageOut[] }>(`/api/chat/sessions/${id}`, { headers: auth });
      setMessages(r.data.messages);
    } catch {
      setMessages([]);
    }
  };

  useEffect(() => {
    reloadSessions();
    reloadQuota();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (activeId) loadSession(activeId);
    else setMessages([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, busy]);

  const send = async (override?: string) => {
    if (!token || busy) return;
    const content = (override ?? draft).trim();
    if (!content) return;
    setBusy(true);
    setError(null);
    setDraft("");
    setMessages((cur) => [
      ...cur,
      { id: -Date.now(), role: "user", content, tool_name: null, created_at: new Date().toISOString() },
    ]);
    try {
      const r = await axios.post<SendOut>(
        "/api/chat/messages",
        { session_id: activeId, content },
        { headers: auth, timeout: 60_000 },
      );
      if (!activeId) {
        setActiveId(r.data.session_id);
        reloadSessions();
      }
      setMessages((cur) => [
        ...cur,
        {
          id: r.data.assistant_message_id,
          role: "assistant",
          content: r.data.assistant_text,
          tool_name: r.data.tool_calls_used.map((t) => t.tool_name).join(",") || null,
          created_at: new Date().toISOString(),
        },
      ]);
      setQuota((q) => q ? { ...q, used_today: q.used_today + 1, remaining: r.data.quota_remaining } : q);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? "전송 실패");
    } finally {
      setBusy(false);
    }
  };

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
  };

  const deleteSession = async (id: number) => {
    if (!token || !confirm("이 대화를 삭제할까요?")) return;
    await axios.delete(`/api/chat/sessions/${id}`, { headers: auth });
    if (activeId === id) {
      setActiveId(null);
      setMessages([]);
    }
    reloadSessions();
  };

  // β-8 v3 — hydration 완료 전엔 깜빡임 X (잘못된 "로그인 필요" 화면 방지)
  if (!hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2 text-text-sub text-sm">
          <span className="w-4 h-4 border-2 border-text-sub/30 border-t-primary rounded-full animate-spin" />
          <span>로딩 중...</span>
        </div>
      </div>
    );
  }
  if (!isLoggedIn) {
    return (
      <div className="px-4 py-12 text-center">
        <p className="text-text-sub text-sm">로그인이 필요해요</p>
        <Link href="/login" className="btn-primary text-sm mt-4 inline-block">로그인</Link>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="🤖 AI 비서" backHref="/mypage" />
      <div className="flex flex-col lg:flex-row" style={{ minHeight: "calc(100vh - 56px)" }}>
      {/* 사이드바 — 세션 목록 (PC) / 상단 (모바일) */}
      <aside className="lg:w-72 lg:border-r lg:border-border lg:bg-bg-secondary/30 lg:overflow-y-auto shrink-0">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-bold text-text">🤖 AI 비서</h2>
          <button onClick={newChat} className="text-2xs text-primary font-bold">+ 새 대화</button>
        </div>
        {quota && (
          <div className="px-4 py-2 border-b border-border text-2xs">
            <p className="text-text-sub">
              오늘 <span className="text-text font-bold">{quota.used_today}/{quota.daily_quota}</span> 사용 ({quota.tier})
            </p>
            <div className="w-full bg-bg-elevated rounded h-1 mt-1">
              <div
                className="h-full bg-primary rounded transition-all"
                style={{ width: `${quota.daily_quota > 0 ? (quota.used_today / quota.daily_quota * 100) : 0}%` }}
              />
            </div>
          </div>
        )}
        <div className="lg:block flex overflow-x-auto lg:overflow-x-visible">
          {sessions === null ? (
            <p className="text-text-sub text-sm p-4">로딩 중...</p>
          ) : sessions.length === 0 ? (
            <p className="text-text-sub text-2xs p-4 text-center">대화 기록 없음</p>
          ) : (
            sessions.map((s) => (
              <div
                key={s.id}
                onClick={() => setActiveId(s.id)}
                className={`shrink-0 lg:shrink lg:w-full cursor-pointer px-4 py-3 border-b lg:border-b lg:border-r-0 border-border lg:max-w-none max-w-[180px] hover:bg-bg-elevated transition-colors ${
                  activeId === s.id ? "bg-primary/10 border-l-2 border-l-primary" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-bold text-text truncate flex-1">
                    {s.title || "(제목 없음)"}
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                    className="text-text-sub hover:text-red-400 text-2xs shrink-0"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-2xs text-text-sub mt-0.5">
                  {relTime(s.last_message_at)} · {s.message_count}개
                </p>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* 채팅 영역 */}
      <main className="flex-1 flex flex-col min-h-0">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 max-w-3xl mx-auto w-full">
          {messages.filter((m) => m.role !== "tool" && m.content).length === 0 && !busy && (
            <div className="text-center py-10">
              <p className="text-3xl mb-3">🤖</p>
              <p className="text-base font-bold text-text">무엇이든 물어보세요</p>
              <p className="text-xs text-text-sub mt-1">트렌드·매장·사용법·공급처·레시피 등</p>
              {/* 2026-05-12 — 빠른 시작 칩 (사용자가 챗봇 범위 빠르게 인지) */}
              <div className="mt-6 max-w-md mx-auto">
                <p className="text-2xs text-text-sub mb-2">💡 이런 걸 물어볼 수 있어요</p>
                <div className="grid grid-cols-1 gap-1.5 text-left">
                  {[
                    { emoji: "📈", text: "요즘 뜨는 디저트 알려줘" },
                    { emoji: "🏪", text: "이번주 매출 어때?" },
                    { emoji: "🏭", text: "버터떡 어디서 사?" },
                    { emoji: "🍳", text: "두바이초콜릿 어떻게 만들어?" },
                    { emoji: "💰", text: "내가 받을 수 있는 절세 알려줘" },
                    { emoji: "❓", text: "AI 이미지 어떻게 만들어?" },
                  ].map((c) => (
                    <button
                      key={c.text}
                      type="button"
                      onClick={() => send(c.text)}
                      className="card px-3 py-2 text-xs text-text hover:bg-bg-elevated transition-colors flex items-center gap-2"
                    >
                      <span className="text-base">{c.emoji}</span>
                      <span>{c.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {(() => {
            const visible = messages.filter((m) => m.role !== "tool" && m.content);
            return visible.map((m, idx) => {
              const content = m.content ?? "";
              const parsed = m.role === "assistant"
                ? parseMessage(content)
                : { cleaned: content, cards: null, followups: [] as string[] };
              const isLastAssistant = m.role === "assistant" && idx === visible.length - 1;
              return (
                <div key={m.id}>
                  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                      m.role === "user"
                        ? "bg-primary text-bg rounded-br-sm"
                        : "bg-bg-elevated text-text rounded-bl-sm"
                    }`}>
                      <p className="text-sm whitespace-pre-wrap leading-relaxed break-words">{parsed.cleaned}</p>
                      {m.role === "assistant" && m.tool_name && (
                        <p className="text-2xs text-text-sub mt-2 italic">🔧 {m.tool_name}</p>
                      )}
                    </div>
                  </div>
                  {parsed.cards && <ChatCards block={parsed.cards} />}
                  {m.role === "assistant" && (
                    <FeedbackButtons messageId={m.id} initialRating={m.feedback ?? null} />
                  )}
                  {isLastAssistant && parsed.followups.length > 0 && !busy && (
                    <div className="mt-2 flex flex-col gap-1.5 max-w-[80%]">
                      {parsed.followups.map((q) => (
                        <button
                          key={q}
                          type="button"
                          onClick={() => send(q)}
                          className="text-xs text-left px-3 py-2 rounded-full bg-bg-elevated border border-border hover:border-primary/60 hover:text-primary transition-colors w-fit"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            });
          })()}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-bg-elevated text-text-sub px-4 py-3 rounded-2xl rounded-bl-sm text-sm">
                <span className="inline-block w-2 h-2 rounded-full bg-text-sub animate-pulse mr-1" />
                AI 가 생각 중...
              </div>
            </div>
          )}
        </div>

        {/* 입력 */}
        <div className="border-t border-border p-3 max-w-3xl mx-auto w-full">
          {error && <p className="text-2xs text-red-400 mb-2 px-1">{error}</p>}
          <div className="flex gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={
                quota && quota.remaining <= 0
                  ? "오늘 한도 소진 — 내일 0시 리셋"
                  : "무엇이든 물어보세요 (Enter 전송, Shift+Enter 줄바꿈)"
              }
              rows={2}
              maxLength={2000}
              disabled={busy || (quota?.remaining ?? 1) <= 0}
              className="flex-1 px-3 py-2 text-sm bg-bg-elevated border border-border rounded-xl text-text resize-none disabled:opacity-50"
            />
            <button
              onClick={() => send()}
              disabled={busy || !draft.trim() || (quota?.remaining ?? 1) <= 0}
              className="px-5 py-2 bg-primary text-bg rounded-xl text-sm font-bold disabled:opacity-30"
            >
              전송
            </button>
          </div>
        </div>
      </main>
      </div>
    </div>
  );
}
